export default {
    install(app) {
        const notify = {
            /**
             * Send a notification using browser notifications if available, 
             * fallback to toast notification
             * @param {Object} options - Notification options
             * @param {string} options.title - Notification title
             * @param {string} options.body - Notification body/message
             * @param {string} options.icon - Notification icon path (optional)
             * @param {string} options.severity - Toast severity level (optional): success, info, warn, error
             * @param {number} options.life - Toast duration in ms (optional, default: 5000)
             * @param {boolean} options.fallbackToToast - Whether to fallback to toast if notifications fail (default: true)
             */
            send(options = {}) {
                const {
                    title = 'Notification',
                    body = '',
                    icon = '/favicon.ico',
                    life = 5000,
                    fallbackToToast = true
                } = options;

                // Check if notifications are supported
                if (!("Notification" in window)) {
                    if (fallbackToToast) {
                        this.toast({
                            severity: 'warn',
                            summary: 'Browser Not Supported',
                            detail: 'Notifications are not supported in this browser',
                            life: life
                        });
                    }
                    return Promise.resolve(false);
                }

                // If permission is already granted, send notification
                if (Notification.permission === "granted") {
                    try {
                        new Notification(title, {
                            body: body,
                            icon: icon
                        });

                        // Optional: Show success toast
                        if (fallbackToToast) {
                            this.toast({
                                severity: 'success',
                                summary: 'Notification Sent',
                                detail: title,
                                life: 3000
                            });
                        }

                        return Promise.resolve(true);
                    } catch (error) {
                        console.error('Failed to send notification:', error);
                        if (fallbackToToast) {
                            this.toast({
                                severity: 'error',
                                summary: 'Notification Failed',
                                detail: 'Failed to send notification',
                                life: life
                            });
                        }
                        return Promise.resolve(false);
                    }
                }

                // If permission is denied, show toast
                if (Notification.permission === "denied") {
                    if (fallbackToToast) {
                        this.toast({
                            severity: 'warn',
                            summary: 'Notifications Blocked',
                            detail: 'Notifications are blocked. Please enable them in your browser settings.',
                            life: life
                        });
                    }
                    return Promise.resolve(false);
                }

                // Request permission
                return Notification.requestPermission().then(permission => {
                    if (permission === "granted") {
                        try {
                            new Notification(title, {
                                body: body,
                                icon: icon
                            });

                            if (fallbackToToast) {
                                this.toast({
                                    severity: 'success',
                                    summary: 'Notification Sent',
                                    detail: title,
                                    life: 3000
                                });
                            }

                            return true;
                        } catch (error) {
                            console.error('Failed to send notification:', error);
                            if (fallbackToToast) {
                                this.toast({
                                    severity: 'error',
                                    summary: 'Notification Failed',
                                    detail: 'Failed to send notification',
                                    life: life
                                });
                            }
                            return false;
                        }
                    } else {
                        if (fallbackToToast) {
                            this.toast({
                                severity: 'warn',
                                summary: 'Permission Denied',
                                detail: 'Notification permission was denied',
                                life: life
                            });
                        }
                        return false;
                    }
                });
            },

            /**
             * Quick notification methods with predefined severity levels
             */
            success(title, body, options = {}) {
                return this.send({
                    title,
                    body,
                    severity: 'success',
                    ...options
                });
            },

            info(title, body, options = {}) {
                return this.send({
                    title,
                    body,
                    severity: 'info',
                    ...options
                });
            },

            warn(title, body, options = {}) {
                return this.send({
                    title,
                    body,
                    severity: 'warn',
                    ...options
                });
            },

            error(title, body, options = {}) {
                return this.send({
                    title,
                    body,
                    severity: 'error',
                    ...options
                });
            },

            /**
             * Toast-only notification (no browser notification)
             */
            toast(options = {}) {
                const {
                    severity = 'info',
                    summary = 'Notification',
                    detail = '',
                    life = 5000
                } = options;

                // Get the current component instance's toast
                const currentInstance = app.config.globalProperties;
                if (currentInstance && currentInstance.$toast) {
                    currentInstance.$toast.add({
                        severity,
                        summary,
                        detail,
                        life
                    });
                } else {
                    console.warn('Toast service not available');
                }
            }
        };

        // Make toast accessible within notify
        notify.toast = function(options) {
            const currentInstance = app.config.globalProperties;
            if (currentInstance && currentInstance.$toast) {
                currentInstance.$toast.add(options);
            } else {
                console.warn('Toast service not available');
            }
        };

        // Add to global properties
        app.config.globalProperties.$Notify = notify;
        
        // Provide for composition API
        app.provide('$Notify', notify);
    }
};
